/**
 * The coarse, React-visible slice of the room.
 *
 * Values that change every tick (hp, mana, projectile positions) are read
 * straight off `net.room.state` by the renderer and the HUD's animation frame.
 * Only things that change a few times a match land here, so React re-renders
 * stay rare.
 */
import { create } from 'zustand';
import { PRACTICE, PROJECTILE_SPEED_SCALE, type RoomMode } from '@spell/shared-types';
import type { Spell, SpellPublic } from '@spell/spell-schema';
import type { MatchResultMsg } from '@spell/network-protocol';

export interface PlayerMeta {
  sessionId: string;
  name: string;
  connected: boolean;
  ready: boolean;
  side: number;
  score: number;
  usingFallbackInput: boolean;
  micGranted: boolean;
  latencyMs: number;
  rematchReady: boolean;
  craftSubmitted: boolean;
}

/** The practice dials, mirrored from room state so the panel shows server truth. */
export interface PracticeMeta {
  fire: boolean;
  intervalMs: number;
}

export interface NetUiState {
  status: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error';
  roomCode: string;
  sessionId: string;
  /** Which kind of room this is. Practice rooms have no clock and no score. */
  mode: RoomMode;
  phase: string;
  round: number;
  phaseEndsAt: number;
  players: PlayerMeta[];
  /**
   * The room's projectile speed scale, as a multiple of each spell's own speed.
   * A room rule both duellists are under, published so neither is surprised.
   */
  speedScale: number;
  practice: PracticeMeta;
  /** Full spell documents this player owns, keyed by spell id. */
  ownSpells: Record<string, Spell>;
  /** Stable, ordered list of the spells this player owns. Read by the HUD. */
  slots: string[];
  opponentSpells: SpellPublic[];
  announcement: { kind: string; text: string; seconds?: number; at: number } | null;
  craft: { open: boolean; slot: number; endsAt: number; round: number } | null;
  lastCastNotes: string[];
  matchResult: MatchResultMsg | null;
  abortReason: string;
  fatalError: string | null;

  set: (patch: Partial<NetUiState>) => void;
  addSpell: (spell: Spell) => void;
  /** Replace the whole book from the server's authoritative list. */
  setSpells: (spells: Spell[]) => void;
  reset: () => void;
}

const initial = {
  status: 'idle' as const,
  roomCode: '',
  sessionId: '',
  mode: 'duel' as RoomMode,
  phase: 'waiting',
  round: 0,
  phaseEndsAt: 0,
  players: [] as PlayerMeta[],
  speedScale: PROJECTILE_SPEED_SCALE.default,
  practice: {
    fire: true,
    intervalMs: PRACTICE.FIRE_INTERVAL_MS.default,
  } as PracticeMeta,
  ownSpells: {} as Record<string, Spell>,
  slots: [] as string[],
  opponentSpells: [] as SpellPublic[],
  announcement: null,
  craft: null,
  lastCastNotes: [] as string[],
  matchResult: null,
  abortReason: '',
  fatalError: null,
};

export const useNet = create<NetUiState>((set) => ({
  ...initial,
  set: (patch) => set(patch),
  addSpell: (spell) =>
    set((s) => {
      const ownSpells = { ...s.ownSpells, [spell.id]: spell };
      return { ownSpells, slots: orderSlots(ownSpells) };
    }),
  setSpells: (spells) =>
    set(() => {
      const ownSpells: Record<string, Spell> = {};
      for (const spell of spells) ownSpells[spell.id] = spell;
      return { ownSpells, slots: orderSlots(ownSpells) };
    }),
  reset: () => set({ ...initial }),
}));

/**
 * Order is stable and meaningful: the parry spell first, then signature spells
 * in the order they were forged — the lobby's attack spell, then each one
 * crafted between rounds. Nothing indexes into this any more, but the HUD reads
 * it top to bottom, and a list that reshuffled mid-match would move the row a
 * player had just learned to look for.
 */
function orderSlots(ownSpells: Record<string, Spell>): string[] {
  const rank = (x: Spell) => (x.type === 'parry' ? 0 : 1);
  return Object.values(ownSpells)
    .sort((a, b) => (rank(a) !== rank(b) ? rank(a) - rank(b) : a.createdAt - b.createdAt))
    .map((x) => x.id);
}

/** Convenience selectors used across the UI. */
export const selectSelf = (s: NetUiState): PlayerMeta | undefined =>
  s.players.find((p) => p.sessionId === s.sessionId);
export const selectOpponent = (s: NetUiState): PlayerMeta | undefined =>
  s.players.find((p) => p.sessionId !== s.sessionId);
/**
 * The spell the client currently *believes* is being cast, for display only.
 *
 * There is no selection any more — this follows the live guess in `castState`,
 * which the server re-derives from the same evidence when the cast lands.
 */
export const selectGuessedSpell = (s: NetUiState, guessId: string): Spell | undefined =>
  s.ownSpells[guessId];
